import { useMemo } from 'react';
import type { CargoItemStatusType, WarehouseRes } from 'api/swagger';
import {
  CARGO_STATUSES,
  CARGO_STATUS_DELIVERED,
  CARGO_STATUS_LOADED,
  CARGO_STATUS_PLANNED,
  CARGO_STATUS_WAITING,
  CARGO_STATUS_WAREHOUSE,
} from 'types/cargo.ts';
import { useGetManyWarehouses } from '../../api/warehouses/use-get-many-warehouses.ts';

interface IFilterValue {
  type: CargoItemStatusType;
  fromWarehouseId?: number;
  toWarehouseId?: number | null;
}

interface IStatusesResult {
  statusesMap: Map<string, IFilterValue>;
  statusesList: string[];
}

interface IGetAllStatusesOptions {
  includeFromStatuses?: boolean;
}

const getAllStatuses = (warehouses?: WarehouseRes[], options?: IGetAllStatusesOptions) => {
  const { includeFromStatuses = false } = options || {};
  const initAccum: IStatusesResult = { statusesMap: new Map(), statusesList: [] };
  if (!warehouses || !warehouses.length) return initAccum;

  const result = CARGO_STATUSES.reduce<IStatusesResult>((accum, status) => {
    const map = accum.statusesMap;
    const list = accum.statusesList;

    if (status === CARGO_STATUS_WAITING || status === CARGO_STATUS_DELIVERED) {
      map.set(status, { type: status });
      list.push(status);
    } else if (status === CARGO_STATUS_PLANNED || status === CARGO_STATUS_LOADED) {
      warehouses.forEach((warehouse) => {
        map.set(`${status} to ${warehouse.name}`, { type: status, toWarehouseId: warehouse.id });
        list.push(`${status} to ${warehouse.name}`);
      });
      map.set(`${status} to client`, { type: status, toWarehouseId: null });
      list.push(`${status} to client`);

      if (includeFromStatuses) {
        warehouses.forEach((warehouse) => {
          map.set(`${status} from ${warehouse.name}`, { type: status, fromWarehouseId: warehouse.id });
          list.push(`${status} from ${warehouse.name}`);
        });
      }
    } else if (status === CARGO_STATUS_WAREHOUSE) {
      warehouses.forEach((warehouse) => {
        map.set(`${status} in ${warehouse.name}`, { type: status, fromWarehouseId: warehouse.id });
        list.push(`${status} in ${warehouse.name}`);
      });
    } else {
      map.set(status, { type: status });
      list.push(status);
    }

    return { statusesMap: map, statusesList: list };
  }, initAccum);

  return result;
};

export const useGetAllStatuses = (options?: IGetAllStatusesOptions): IStatusesResult => {
  const { data: warehouses } = useGetManyWarehouses({ withDeprecated: false });

  return useMemo(() => getAllStatuses(warehouses, options), [warehouses, options]);
};
